<script setup>
import { SOCIAL_SHARE_IMAGE, usePageHead } from '@composables/use-page-head'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { useKeyControlsService } from '@composables/use-key-controls-service'
import { useSearchStore } from '@state/stores/search'
import Layout from '@layouts/main-layout'
import SearchField from '@components/search/search-field'
import recentSearchService from '@services/search/recent-search-service'

const RECENT_SEARCH_OPTIONS = ['artist', 'genre', 'mix', 'show']
// Phone live results follow typing after this pause — the delay master's page field used
// (BaseSearchInput debounceDelay; Faisal 2026-09-17).
const LIVE_SEARCH_DELAY_MS = 500

const route = useRoute()
const router = useRouter()
const searchStore = useSearchStore()
const keyControlsService = useKeyControlsService()
const phoneField = ref(null)
const phoneKeyword = ref('')
let liveSearchTimer = null
let hasTyped = false

const searchKeyword = computed(() => searchStore.keyword)
const routeKeyword = computed(() => route.params?.keyword)

onBeforeRouteLeave(to => {
    const itemId = to.params.id || to.params.slug
    if (RECENT_SEARCH_OPTIONS.includes(to.name) && itemId && !to.query.back) {
        recentSearchService.addToRecent({
            id: `${to.name}-${itemId}`,
            type: to.name,
            itemId,
        })
    }
    return true
})

watch(searchKeyword, keyword => {
    if (keyword) {
        router.push({
            name: route.name.includes('search/results') ? route.name : 'search/results/top',
            params: { keyword: keyword.replace(/ /g, '_') },
        })
    } else {
        router.push({ name: 'search' })
    }
})

// The phone field mirrors the URL keyword (the top bar does the same from tablet up).
watch(routeKeyword, keyword => {
    if (!keyword) return
    const routeValue = keyword.replace(/_/g, ' ')
    // A route that only catches up with this page's own live update (the push resolves
    // after the next keystroke) must not overwrite what the listener typed since.
    const isOwnLiveUpdate = hasTyped && routeValue.trim() === searchStore.keyword
    if (!isOwnLiveUpdate && phoneKeyword.value.trim() !== routeValue.trim()) phoneKeyword.value = routeValue
    searchStore.updateKeyword(routeValue)
}, { immediate: true })

function cancelLiveSearch() {
    if (liveSearchTimer === null) return
    clearTimeout(liveSearchTimer)
    liveSearchTimer = null
}

// User input only (SearchField emits update:modelValue from its input event and its clear
// disc). An emptied field returns to the search landing once the pause passes, as on master.
function onPhoneInput(value) {
    phoneKeyword.value = value
    hasTyped = true
    cancelLiveSearch()
    liveSearchTimer = setTimeout(() => {
        liveSearchTimer = null
        searchStore.updateKeyword(value.trim())
    }, LIVE_SEARCH_DELAY_MS)
}

function onPhoneSearch(keyword) {
    cancelLiveSearch()
    searchStore.updateKeyword(keyword)
}

// The clear disc empties the field only: its update:modelValue('') must not reach the store.
function onPhoneClear() {
    cancelLiveSearch()
    phoneKeyword.value = ''
}

// No auto-focus: the first Search tab tap must not raise the keyboard (phone amendment
// 2026-09-10). The Search re-tap focuses the field through this event (tab-bar.vue).
function focusPhoneField() {
    phoneField.value?.focus()
}

onMounted(() => keyControlsService.on('focusSearch', focusPhoneField))
onBeforeUnmount(() => {
    cancelLiveSearch()
    keyControlsService.off('focusSearch', focusPhoneField)
})

usePageHead({
    title: 'Search',
    description: 'Search FRISKY for mixes, shows, artists, and genres.',
    image: SOCIAL_SHARE_IMAGE,
})
</script>

<template>
    <Layout>
        <!-- Phone-only field block, sticky under the phone top bar (D19/D26); from the tablet tier up the top bar carries the field -->
        <div class="phone-search-field">
            <SearchField
                ref="phoneField"
                :model-value="phoneKeyword"
                layout="phone"
                @update:model-value="onPhoneInput"
                @submit="onPhoneSearch"
                @clear="onPhoneClear"
            />
        </div>
        <BaseContent class="search-area">
            <router-view v-slot="{ Component }">
                <transition
                    name="fade"
                    mode="out-in"
                >
                    <component :is="Component" />
                </transition>
            </router-view>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.search-area {
    min-height: calc(100vh - #{$size-phone-top-bar-height} - env(safe-area-inset-top) - #{$size-search-field-block} - #{$size-tab-bar-height} - #{$size-music-player});

    @include tablet {
        min-height: calc(100vh - #{$size-music-player});
    }
}

// Figma 534:1238: the 358×48 pill at the top of the page content (20 above in the frame →
// 16, the page gutter and nearest scale step), sticky under the phone top bar. Canvas
// background so results scroll under it, not through it. Side insets: `body > #app`
// already pads them, and this block lives inside it (not a fixed full-width bar).
.phone-search-field {
    position: sticky;
    top: calc(#{$size-phone-top-bar-height} + env(safe-area-inset-top));
    z-index: $layer-dropdown-z-index;
    height: $size-search-field-block;
    padding: $space-16 $space-16 $space-12;
    background: $c-surface-canvas;

    @include tablet {
        display: none;
    }
}
</style>
